import { devLog } from '@/utils'
import { showSettings } from '@/utils/io/deviceSettings'

const { cordova } = window

export const GEOLOCATION_NOT_REQUESTED = -1
export const GEOLOCATION_DENIED = 0
export const GEOLOCATION_GRANTED_WHEN_IN_USE = 1
export const GEOLOCATION_GRANTED = 2

const _getLocationAuthorisationStatusResolver = (resolve) => (s) => {
  switch (s) {
    case cordova.plugins.diagnostic.permissionStatus.NOT_REQUESTED:
      resolve(GEOLOCATION_NOT_REQUESTED)
      break
    case cordova.plugins.diagnostic.permissionStatus.GRANTED:
      resolve(GEOLOCATION_GRANTED)
      break
    case cordova.plugins.diagnostic.permissionStatus.GRANTED_WHEN_IN_USE:
      resolve(GEOLOCATION_GRANTED_WHEN_IN_USE)
      break
    default:
      resolve(GEOLOCATION_DENIED)
  }
}

/**
 * Vérifie si le GPS est activé sur l'appareil.
 *
 * @returns {Promise<Number>}
 */
export const checkLocationStatus = async () => {
  if (cordova) {
    return new Promise((resolve) => {
      return cordova.plugins.diagnostic
        .getLocationAuthorizationStatus(
          _getLocationAuthorisationStatusResolver(resolve),
          () => {
            resolve(GEOLOCATION_DENIED)
          }
        )
    })
  }

  try {
    await getCurrentPosition()
  } catch ({ error }) {
    console.error(error)
    if (error.code === (window.GeolocationPositionError && window.GeolocationPositionError.PERMISSION_DENIED)) {
      return GEOLOCATION_DENIED
    }
  }
  return GEOLOCATION_GRANTED_WHEN_IN_USE
}

export const requestLocationAuthorisation = (always = false) =>
  new Promise((resolve, reject) => cordova
    ? cordova.plugins.diagnostic.requestLocationAuthorization(
      _getLocationAuthorisationStatusResolver(resolve),
      (error) => {
        devLog(error)
        reject(error)
      },
      (always
        ? cordova.plugins.diagnostic.locationAuthorizationMode.ALWAYS
        : cordova.plugins.diagnostic.locationAuthorizationMode.WHEN_IN_USE)
    )
    : resolve(true)
  )

export const goToLocationSettings = () => {
  showSettings(
    'locations',
    () => showSettings(
      'location'
    )
  )
}

/**
 * Retourne le résultat de navigator.geolocation.getCurrentPosition dans un Promise
 * @returns {Promise<{}>}
 */
export const getCurrentPosition = (opt = {}) => {
  return new Promise((resolve, reject) => {
    if (process.env.DEV && window.vposition) {
      // console.info('[DEV] Utilisation de la position virtuelle (window.vposition) : ', window.vposition)
      return resolve({ coords: window.vposition })
    }
    navigator.geolocation.getCurrentPosition(
      resolve,
      (error) => {
        devLog(error)
        reject({
          message: `Impossible de localiser votre position. Si le problème persiste merci de réinstaller l'application mobile ${process.env.APP.name}.`,
          error
        })
      },
      {
        enableHighAccuracy: true,
        timeout: 15000, // timeout de 15 secondes
        ...opt
      })
  })
}

/**
 * Retourne un objet location
 * @returns {Promise<{latitude: Number, longitude: Number}>}
 */
export const getLocation = async () => {
  const {
    coords: {
      latitude,
      longitude,
      accuracy,
      altitude
    }
  } = await getCurrentPosition()
  return {
    latitude,
    longitude,
    accuracy,
    altitude
  }
}

/**
 * Converti des coordonnées gmap vers des coordonnées Lwf/Location
 * @param lat
 * @param lng
 * @returns {{latitude: *, longitude: *}}
 */
export const gmapToLwf = ({
  lat,
  lng
}) => ({
  latitude: typeof lat === 'function' ? lat() : lat,
  longitude: typeof lng === 'function' ? lng() : lng
})

/**
 * Converti des coordonnées Lwf/Location vers des coordonnées gmap
 * @param latitude
 * @param longitude
 * @returns {{lng: *, lat: *}|null}
 */
export const lwfToGmap = ({
  latitude,
  longitude
} = {}) => (
  latitude && longitude
    ? {
      lat: latitude,
      lng: longitude
    }
    : null
)

/**
 * Convertion d'un site en cercle pour GMAP
 * @param site
 * @return {*}
 */
export const siteToGmapCircle = (site) => {
  const center = lwfToGmap(_.get(site, ['address', 'location']))
  return center
    ? {
      options: {
        strokeColor: '#FF0000',
        strokeOpacity: 0.5,
        strokeWeight: 2,
        fillColor: '#FF0000',
        fillOpacity: 0.25
      },
      radius: site.radius || 0,
      center: center
    }
    : null
}

/**
 * Récupère une info sur des gmapResults
 * @param keyedInfos
 * @param k
 * @returns {*}
 * @private
 */
const _getInfo = (keyedInfos, k) => _.get(keyedInfos, [k, 'long_name'])

/**
 * Transforme un résultat de géocodage en Lwf/Address
 * @param address_components
 * @param location
 * @returns {{zipcode: *, city: *, street: string, location: {latitude: *, longitude: *}}}
 */
export const gmapResultToAddress = ({
  // eslint-disable-next-line camelcase
  address_components,
  // eslint-disable-next-line camelcase
  formatted_address,
  geometry: { location }
}) => {
  const keyedInfos = _.keyBy(address_components, 'types.0')
  return {
    street: [_getInfo(keyedInfos, 'street_number'), _getInfo(keyedInfos, 'route')].join(' '),
    city: _getInfo(keyedInfos, 'locality'),
    zipcode: _getInfo(keyedInfos, 'postal_code'),
    country: _getInfo(keyedInfos, 'country'),
    location: gmapToLwf(location),
    value: formatted_address,
    label: formatted_address
  }
}

/**
 * Retourne la ville
 * @param {$gmapApiPromiseLazy} $gmapApiPromiseLazy
 * @returns {Promise<*>}
 */
export const getCity = async ($gmapApiPromiseLazy) => {
  const location = await getLocation()
  const { maps } = await $gmapApiPromiseLazy()
  const geocoder = new maps.Geocoder()

  return new Promise((resolve, reject) => {
    geocoder.geocode(
      { location: lwfToGmap(location) },
      (results, status) => {
        if (!results || !results.length) {
          reject('Impossible de localiser la ville.')
        }

        resolve(gmapResultToAddress(results[0]).city)
      })
  })
}
